import React from 'react';
import { Badge, Typography } from 'antd';
import CustomerName from '@/components/Customer/CustomerName';
import { PrettyDate } from '@/components/PrettyDate';
import { CustomerModel } from '@/typings/models/Customer';

export const CUSTOMER_PROPERTIES = {
  NAME: 'name',
  EMAIL: 'email',
  LOCALE: 'locale',
  PHONE: 'phone',
  CREATED_AT: 'createdAt',
  STATUS: 'customer_status',
  NOTE: 'note',

  ORDER_VOLUME: 'order_volume',
  UTM_MEDIUM: 'utm_medium',
  UTM_CAMPAIGN: 'utm_campaign',
  RESULT: 'result',
  SUBSCRIBER: 'subscriber',
  DOI_DATE: 'doi_date',
}

export const CUSTOMER_VISIBILITY_SETTINGS_PLACES = {
  CUSTOMERS_TABLE: 'customersTableVisibilitySettings',
};

export const INITIAL_PROPERTY_VISIBILITY_SETTINGS = {
  shown: [
    CUSTOMER_PROPERTIES.STATUS,
    CUSTOMER_PROPERTIES.EMAIL,
    CUSTOMER_PROPERTIES.LOCALE,
    CUSTOMER_PROPERTIES.PHONE,
    CUSTOMER_PROPERTIES.CREATED_AT,
    CUSTOMER_PROPERTIES.NOTE,
    CUSTOMER_PROPERTIES.UTM_MEDIUM,
    CUSTOMER_PROPERTIES.UTM_CAMPAIGN,
    CUSTOMER_PROPERTIES.RESULT
  ],
};

export const INITIAL_CUSTOMERS_TABLE_VISIBILITY_SETTINGS = {
  shown: [
    CUSTOMER_PROPERTIES.NAME,
    CUSTOMER_PROPERTIES.EMAIL,
    CUSTOMER_PROPERTIES.STATUS,
    CUSTOMER_PROPERTIES.UTM_MEDIUM,
    CUSTOMER_PROPERTIES.CREATED_AT,
  ],
  hidden: [
    CUSTOMER_PROPERTIES.PHONE,
    CUSTOMER_PROPERTIES.LOCALE,
    CUSTOMER_PROPERTIES.SUBSCRIBER,
    CUSTOMER_PROPERTIES.DOI_DATE,
    CUSTOMER_PROPERTIES.NOTE,
    CUSTOMER_PROPERTIES.ORDER_VOLUME,
    CUSTOMER_PROPERTIES.RESULT,
    CUSTOMER_PROPERTIES.UTM_CAMPAIGN,
  ],
};

export const CUSTOMER_PROPERTY_COLUMN_SETTINGS = {
  [CUSTOMER_PROPERTIES.NAME]: {
    title: {
      id: 'pages.customers.name',
      defaultMessage: 'Name',
    },
    extraProps: {
      fixed: 'left',
    },
  },
  [CUSTOMER_PROPERTIES.EMAIL]: {
    title: {
      id: 'pages.customers.email',
      defaultMessage: 'Email',
    },
  },
  [CUSTOMER_PROPERTIES.LOCALE]: {
    title: {
      id: 'pages.lead.fields.locale',
      defaultMessage: 'Language',
    },
  },
  [CUSTOMER_PROPERTIES.PHONE]: {
    title: {
      id: 'common.attribute.phone',
      defaultMessage: 'Phone',
    },
  },
  [CUSTOMER_PROPERTIES.STATUS]: {
    title: {
      id: 'pages.customers.status',
      defaultMessage: 'Status',
    },
  },
  [CUSTOMER_PROPERTIES.CREATED_AT]: {
    title: {
      id: 'pages.customers.createDate',
      defaultMessage: 'Create Date',
    },
  },
  [CUSTOMER_PROPERTIES.NOTE]: {
    title: {
      id: 'pages.lead.fields.note',
      defaultMessage: 'Notes',
    },
  },

  [CUSTOMER_PROPERTIES.ORDER_VOLUME]: {
    title: {
      id: 'pages.customers.orderVolume',
      defaultMessage: 'Order Volume',
    },
  },
  [CUSTOMER_PROPERTIES.UTM_MEDIUM]: {
    title: {
      id: 'pages.customers.utmMedium',
      defaultMessage: 'UTM Medium',
    },
  },
  [CUSTOMER_PROPERTIES.UTM_CAMPAIGN]: {
    title: {
      id: 'pages.lead.fields.utm_campaign',
      defaultMessage: 'UTM Campaign',
    },
  },
  [CUSTOMER_PROPERTIES.RESULT]: {
    title: {
      id: 'pages.customers.result',
      defaultMessage: 'Result',
    },
  },
  [CUSTOMER_PROPERTIES.SUBSCRIBER]: {
    title: {
      id: 'pages.customers.activeRecipient',
      defaultMessage: 'Active Recipient',
    },
  },
  [CUSTOMER_PROPERTIES.DOI_DATE]: {
    title: {
      id: 'pages.customers.doubleOptin',
      defaultMessage: 'Double Opt-in',
    },
  },
};

export const CUSTOMER_TABLE_SORT_KEYS = {
  [CUSTOMER_PROPERTIES.NAME]: 'name',
  [CUSTOMER_PROPERTIES.EMAIL]: 'email',
  [CUSTOMER_PROPERTIES.CREATED_AT]: 'created',
  [CUSTOMER_PROPERTIES.UTM_MEDIUM]: 'utm_medium',
  [CUSTOMER_PROPERTIES.PHONE]: 'phone',
};

export const DEFAULT_CUSTOMER_VISIBILITY_SETTINGS = {
  [CUSTOMER_VISIBILITY_SETTINGS_PLACES.CUSTOMERS_TABLE]: INITIAL_CUSTOMERS_TABLE_VISIBILITY_SETTINGS,
};

export const CUSTOMER_DETAILS_VISIBLE_PROPERTIES = [
  CUSTOMER_PROPERTIES.STATUS,
  CUSTOMER_PROPERTIES.EMAIL,
  CUSTOMER_PROPERTIES.PHONE,
  CUSTOMER_PROPERTIES.NOTE,
  CUSTOMER_PROPERTIES.CREATED_AT,
];

export const CustomerTablePropertyComponent = ({
  customer,
  property,
}: {
  customer: CustomerModel;
  property: string;
}) => {
  switch (property) {
    case CUSTOMER_PROPERTIES.NAME:
      return <CustomerName drawer customer={customer} />;
    case CUSTOMER_PROPERTIES.EMAIL:
      return customer?.email ? (
        <Typography.Text style={{ whiteSpace: 'nowrap' }} copyable={{ text: customer.email }}>
          {customer.email}
        </Typography.Text>
      ) : <span>-</span>;
    case CUSTOMER_PROPERTIES.PHONE:
      return <span style={{ whiteSpace: 'nowrap' }}>{customer?.phone || '-'}</span>;
    case CUSTOMER_PROPERTIES.LOCALE:
      return <span>{customer?.locale || '-'}</span>;
    case CUSTOMER_PROPERTIES.STATUS:
      return <span>{typeof customer?.state === 'object' ? customer?.state?.label : customer?.state || '-'}</span>;
    case CUSTOMER_PROPERTIES.CREATED_AT:
      return customer?.created ? <PrettyDate date={customer.created} /> : <span>-</span>;
    case CUSTOMER_PROPERTIES.UTM_MEDIUM:
      return <span>{customer?.utm_medium || '-'}</span>;
    case CUSTOMER_PROPERTIES.UTM_CAMPAIGN:
      return <span>{customer?.utm_campaign || '-'}</span>;
    case CUSTOMER_PROPERTIES.NOTE:
      return customer?.notes ? (
        <Typography.Paragraph ellipsis={{ rows: 2 }} style={{ marginBottom: 0, maxWidth: 200 }}>
          {customer.notes}
        </Typography.Paragraph>
      ) : <span>-</span>;
    case CUSTOMER_PROPERTIES.SUBSCRIBER:
      return <Badge color={customer?.subscriber ? 'green' : 'red'} />;
    case CUSTOMER_PROPERTIES.DOI_DATE:
      return customer?.doi_date ? <PrettyDate date={customer.doi_date} /> : <span>-</span>;

    case CUSTOMER_PROPERTIES.ORDER_VOLUME:
      return <span>{customer?.order_volume ?? '-'}</span>;
    case CUSTOMER_PROPERTIES.RESULT:
      return customer?.links?.view ? (
        <Typography.Text copyable={{ text: customer.links.view }}>
          <a target="_blank" href={customer.links.view}>Result</a>
        </Typography.Text>
      ) : <span>-</span>;
    default:
      return null;
  }
};
